/**
 * Grouping for the report's "Manuell · zu prüfen" lane.
 *
 * The lane is one of the three honest sections and it is long by design — a
 * real Egg run puts ~17 rules in it. What it was NOT allowed to stay is one
 * flat list of seventeen technical sentences (zone-parameter names, rule ids,
 * formula names) in engine order, with "nicht anwendbar in dieser Zone" sitting
 * above "der Eingabewert fehlt". That ordering tells the reader that the two
 * are the same kind of thing. They are not: one is work the dossier still owes,
 * the other is a rule that does not apply here at all.
 *
 * So: same items, all of them, none hidden — grouped by the kind of reason,
 * with the groups that BLOCK a check above the group that is merely not
 * applicable.
 *
 * The classification reads the engine's own sentences. That is a coupling, and
 * it is a deliberate one: `SkippedRule` carries `reason` as free text
 * (app/schemas.py) and nothing machine-readable, so either the frontend matches
 * on the wording or the lane stays flat. The backend already does exactly this
 * in `app/pipeline.py` (`ZONE_PARAM_MISSING`, `HOEHENREGIME_SKIP`), and the
 * markers below are the same ones. A sentence that matches nothing lands in
 * `other`, which renders like every other group — an unrecognised reason is
 * never dropped and never silently demoted to the bottom.
 */
import type { TranslationKey } from '@/i18n'

import type { SkippedRule } from '@/api/types'

export type SkippedKind =
  | 'missingInput'
  | 'missingGis'
  | 'notComputable'
  | 'unverified'
  | 'other'
  | 'notApplicable'

interface KindSpec {
  kind: SkippedKind
  title: TranslationKey
  lead: TranslationKey
  /** Does this reason stop a check from running (or from being hard)? */
  blocking: boolean
  /** Engine wordings that mean this kind. Tested against the raw `reason`. */
  markers: readonly string[]
}

/**
 * Presentation order. Blocking first, "nicht anwendbar" last, `other` above it
 * because an unclassified reason might well block something and the lane must
 * not quietly file an unknown under "no action needed".
 */
export const SKIPPED_KINDS: readonly KindSpec[] = [
  {
    kind: 'missingInput',
    title: 'report.skippedGroup.missingInput',
    lead: 'report.skippedGroup.missingInputLead',
    blocking: true,
    markers: [
      'Eingabewert',
      'Wert im Formular oder im GIS fehlt',
      'Formularfeld',
      'ist nicht numerisch',
    ],
  },
  {
    kind: 'missingGis',
    title: 'report.skippedGroup.missingGis',
    lead: 'report.skippedGroup.missingGisLead',
    blocking: true,
    markers: ['GIS-Wert'],
  },
  {
    kind: 'notComputable',
    title: 'report.skippedGroup.notComputable',
    lead: 'report.skippedGroup.notComputableLead',
    blocking: true,
    markers: ['nicht rechenbar', 'nicht implementiert'],
  },
  {
    kind: 'unverified',
    title: 'report.skippedGroup.unverified',
    lead: 'report.skippedGroup.unverifiedLead',
    blocking: true,
    markers: ['unverifiziert'],
  },
  {
    kind: 'other',
    title: 'report.skippedGroup.other',
    lead: 'report.skippedGroup.otherLead',
    blocking: true,
    markers: [],
  },
  {
    kind: 'notApplicable',
    title: 'report.skippedGroup.notApplicable',
    lead: 'report.skippedGroup.notApplicableLead',
    blocking: false,
    markers: ['Zonen-Parameter «zone.', 'Höhenregime', 'nicht anwendbar'],
  },
]

export interface SkippedGroup {
  kind: SkippedKind
  title: TranslationKey
  lead: TranslationKey
  blocking: boolean
  rules: SkippedRule[]
}

/**
 * Which group a single reason belongs to.
 *
 * The array is walked in order, which puts the specific markers before the
 * broad ones — `notApplicable` is last and owns the broadest marker of all
 * («nicht anwendbar»). That is what keeps «Eingabewert «x» fehlt» apart from
 * «nicht anwendbar — Zonen-Parameter «zone.x» fehlt»: two different answers,
 * deliberately two different sentences in the engine (see the note on
 * `zonen_parameter_fehlt` in engine/baupilot.py), and a reader who confuses
 * them reads a gap in the dossier as a rule that does not apply.
 */
export function kindOf(rule: SkippedRule): SkippedKind {
  const reason = rule.reason ?? ''
  for (const spec of SKIPPED_KINDS) {
    if (spec.markers.some((marker) => reason.includes(marker))) return spec.kind
  }
  return 'other'
}

/** The lane, grouped. Empty groups are dropped; engine order is kept inside one. */
export function groupSkipped(rules: readonly SkippedRule[]): SkippedGroup[] {
  return SKIPPED_KINDS.map((spec) => ({
    kind: spec.kind,
    title: spec.title,
    lead: spec.lead,
    blocking: spec.blocking,
    rules: rules.filter((rule) => kindOf(rule) === spec.kind),
  })).filter((group) => group.rules.length > 0)
}
